import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { IUser } from '../interfaces/user.interface';
import { isValidDate } from '../validators/date.validator';
import { isInValidMinAge } from '../validators/min-age.validator';
import * as identityCard from 'coreplatform-identity-card-artifact/dist/identity-card-artifact.min';
import {
  ArgosModalConfig,
  ArgosModalPosition,
  ArgosModalRef,
  ArgosModalService,
  ArgosSimpleModalComponent,
  ArgosSimpleModalData
} from '@core-argos-angular-artifact/components';
import { ApiCountryService } from './api-country.service';

@Injectable({
  providedIn: 'root'
})
export class UserValidationService {
  private manageSidebarSubject: Subject<boolean> = new Subject();

  constructor(
    private readonly countriesService: ApiCountryService,
    private readonly modal: ArgosModalService,
    private readonly translateService: TranslateService,
    private readonly router: Router
  ) {}

  validateUser(user: IUser): void {
    if (
      !this.isValidName(user.firstName) ||
      !this.isValidName(user.lastName) ||
      !this.isValidBirthdate(user.birthdate, user.countryCode) ||
      !this.isValidIdentityDocument(
        user.identityDocumentType,
        user.identityDocument
      )
    ) {
      this.openModal(user.firstName);
    } else {
      this.isValidCountry(user.countryCode).subscribe((valid: boolean) => {
        if (!valid) {
          this.openModal(user.firstName);
        }
      });
    }
  }

  changeSidebarState(isShowed: boolean): void {
    this.manageSidebarSubject.next(isShowed);
  }

  manageSidebar(): Observable<boolean> {
    return this.manageSidebarSubject.asObservable();
  }

  cleanUrl(): void {
    this.router.navigate(['/info'], {
      replaceUrl: true
    });
  }

  private isValidName(value: string): boolean {
    return value && value.trim() !== '';
  }

  private isValidCountry(value: string): Observable<boolean> {
    if (!value || value.trim() === '') {
      return of(false);
    }

    return this.countriesService.getCountry(value).pipe(
      first(),
      map(() => true),
      catchError((error: any) => (error.status === 404 ? of(false) : of(true)))
    );
  }

  private isValidBirthdate(date: string, countryCode: string): boolean {
    return (
      date &&
      date.trim() !== '' &&
      isValidDate(date) &&
      !isInValidMinAge(date, countryCode)
    );
  }

  private isValidIdentityDocument(type: string, document: string): boolean {
    return !identityCard.validateIdentityCard(type.toUpperCase(), document);
  }

  private openModal(userName: string = '') {
    this.translateService
      .get(
        [
          _('update-data-modal.title'),
          _('update-data-modal.description'),
          _('update-data-modal.update')
        ],
        { userName }
      )
      .pipe(first())
      .subscribe(translations => {
        const config = {
          data: {
            title: translations['update-data-modal.title'],
            description: translations['update-data-modal.description'],
            textConfirm: translations['update-data-modal.update']
          } as ArgosSimpleModalData,
          position: ArgosModalPosition.CENTER
        } as ArgosModalConfig;

        const modalRef: ArgosModalRef = this.modal.open(
          ArgosSimpleModalComponent,
          config
        ) as ArgosModalRef;

        modalRef.afterClosed.subscribe(result => {
          if (result) {
            this.goToModifyData();
          }
        });
      });
  }

  private goToModifyData(): void {
    this.router.navigate(['/info'], {
      queryParams: { action: 'modify' }
    });
  }
}
